"use client";

import Link from "next/link";
import { SanityDocument } from "next-sanity";
import { useOptimistic } from "next-sanity/hooks";

import BlockRenderer from "@/components/blockRenderer/blockRenderer.component";

import { studioUrl } from "@/sanity/api";
import { dataAttr } from "@/sanity/utils";

export default function PageBuilder({ page }: PageBuilderPageProps) {
  const pageBuilderSections = useOptimistic<
    PageBuilderSection[] | undefined,
    SanityDocument<PageBuilderHost>
  >(
    (page?.pageBuilder as PageBuilderSection[] | undefined) || [],
    (currentSections, action) => {
      // The action contains updated document data from Sanity
      // when someone makes an edit in the Studio

      // If the edit was to a different document, ignore it
      if (action.id !== page?._id) {
        return currentSections;
      }

      // If there are sections in the updated document, use them
      if (action.document.pageBuilder) {
        // Reconcile References. https://www.sanity.io/docs/enabling-drag-and-drop#ffe728eea8c1
        return action.document.pageBuilder.map(
          (section) =>
            currentSections?.find((s) => s._key === section?._key) || section,
        );
      }

      // Otherwise keep the current sections
      return currentSections;
    },
  );

  if (!page) {
    return renderEmptyState(page);
  }

  return pageBuilderSections && pageBuilderSections.length > 0
    ? renderSections(pageBuilderSections, page)
    : renderEmptyState(page);
}

/**
 * Structural contract for any document passed to PageBuilder (regular pages, shop singleton,
 * future singletons). Call sites do not need to extend a union here as long as the query
 * result includes `_id`, `_type`, and optional `pageBuilder`.
 */
export type PageBuilderHost = {
  _id: string;
  _type: string;
  name?: string | null;
  /** Matches GROQ/Sanity: absent, null, or a list of blocks. */
  pageBuilder?: PageBuilderSection[] | null;
};

type PageBuilderPageProps = {
  page: PageBuilderHost | null;
};

type PageBuilderSection = {
  _key: string;
  _type: string;
};

/**
 * The PageBuilder component is used to render the blocks from the `pageBuilder` field in the Page type in your Sanity Studio.
 */

function renderSections(
  pageBuilderSections: PageBuilderSection[],
  page: PageBuilderHost,
) {
  if (!page) {
    return null;
  }
  return (
    <div
      data-sanity={dataAttr({
        id: page._id,
        type: page._type,
        path: `pageBuilder`,
      }).toString()}>
      {pageBuilderSections.map((block: PageBuilderSection, index: number) => (
        <BlockRenderer
          key={block._key}
          index={index}
          block={block}
          pageId={page._id}
          pageType={page._type}
          pageName={page.name}
        />
      ))}
    </div>
  );
}

function renderEmptyState(page: PageBuilderHost | null) {
  if (!page) {
    return null;
  }
  return (
    <div className="container">
      <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
        This page has no content!
      </h1>
      <p className="mt-2 text-base text-gray-500">
        Open the page in Sanity Studio to add content.
      </p>
      <div className="mt-10 flex">
        <Link
          className="hover:bg-brand focus:bg-blue mr-6 flex items-center gap-2 rounded-full bg-black px-6 py-3 text-white transition-colors duration-200"
          href={`${studioUrl}/structure/intent/edit/template=${page._type};type=${page._type};path=pageBuilder;id=${page._id}`}
          target="_blank"
          rel="noopener noreferrer">
          Add content to this page
        </Link>
      </div>
    </div>
  );
}
